import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Accordion, Tab } from 'semantic-ui-react';
import { AccordionContainer } from '@publicisgroupe/p-os-components';
import { v4 as uuidv4 } from 'uuid';

import useFeatureFlags from 'components/FeatureFlagsProvider/hooks/useFeatureFlags';
import useErrorNotification from 'hooks/useErrorNotification';
import { useFAQPageData } from './hooks/useFAQPageData';
import { useHelpSectionFAQData } from './hooks/useHelpSectionFAQData';
import NoData from '../NoData';

import { FaqDto, FrequentQuestion } from 'types/types';
import { t } from 'utils/resources';

import './FAQPage.less';

interface FAQPageProps {
  module: string;
}

interface InnerHTMLProps {
  innerHTMLString: string;
  classNameString?: string;
}

const InnerHTML = ({ innerHTMLString, classNameString = '' }: InnerHTMLProps) => (
  <div
    className={classNameString}
    dangerouslySetInnerHTML={{
      __html: innerHTMLString,
    }}
  />
);

const FAQPage = ({ module }: FAQPageProps) => {
  const location = useLocation();
  const { featureFlags, isLoading } = useFeatureFlags();
  const { showErrorNotification } = useErrorNotification();

  const [activeIndex, setActiveIndex] = useState([0]);
  const [faqResponse, setFaqResponse] = useState<any[]>();
  const [faqData, setFaqData] = useState<FaqDto>();

  const faqPageApiDataRequest = useFAQPageData();
  const faqPageDataRequest = useHelpSectionFAQData();

  const { UseConfigModule: useConfigModule } = featureFlags;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const faqDataResponse = await faqPageApiDataRequest(useConfigModule);

        setFaqResponse(faqDataResponse);
      } catch (error) {
        showErrorNotification({
          errorTitle: t('HelpSectionErrorTitle'),
          content: t('PageInfoErrorMessage'),
        });
      }
    };

    fetchData();
  }, [faqPageApiDataRequest, useConfigModule, showErrorNotification]);

  useEffect(() => {
    if (faqResponse && faqResponse.length > 0) {
      const response = faqPageDataRequest(module, faqResponse, location);

      setFaqData(response);
    }
  }, [faqPageDataRequest, module, faqResponse, location]);

  const onAccordionClick = useCallback(
    (index: number) => {
      if (activeIndex.length > 0) activeIndex?.splice(0, activeIndex.length);

      setActiveIndex([...(activeIndex ?? []), index]);
    },
    [activeIndex]
  );

  if (isLoading || !faqData || faqData.questions.length == 0) return <NoData />;

  return (
    <Tab.Pane>
      <div className="tab-container">
        <div className="faq-container">
          <div className="information">
            <p>{t('HelpSection.FAQ.Header')}</p>
          </div>

          <Accordion className="frequent-questions">
            {faqData.questions.map((faq: FrequentQuestion, index: number) => (
              <AccordionContainer
                key={`frequent-question-${uuidv4()}`}
                activeIndex={activeIndex ?? -1}
                content={
                  <InnerHTML innerHTMLString={faq.answer} classNameString={'answer-content'} />
                }
                headerIconPosition="right"
                index={index}
                onAccordionClick={onAccordionClick}
                title={faq.question}
              />
            ))}
          </Accordion>
        </div>
      </div>
    </Tab.Pane>
  );
};

export default FAQPage;
